export type PermissionModuleType = 'Brooklyn' | 'Paybev' | 'Supplier';

/** Which checkboxes may appear for a permission row (visibility only — never mutates stored flags). */
export interface PermissionCaps {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

export interface PermissionUiFlags {
  _uiShowAll: boolean;
  _uiShowView: boolean;
  _uiShowAdd: boolean;
  _uiShowEdit: boolean;
  _uiShowDelete: boolean;
}

const FULL: PermissionCaps = { view: true, add: true, edit: true, delete: true };
const VIEW_ONLY: PermissionCaps = { view: true, add: false, edit: false, delete: false };
const VIEW_EDIT: PermissionCaps = { view: true, add: false, edit: true, delete: false };

const warnedUnknown = new Set<string>();

const BROOKLYN_CAPS: Record<string, PermissionCaps> = {
  Overview: VIEW_ONLY,
  'Cash Flow': VIEW_ONLY,
  Dashboard: VIEW_ONLY,
  Investments: FULL,
  Expenses: FULL,
  Transfers: VIEW_EDIT,
  Reports: VIEW_ONLY,
  'Brooklyn.Projections': VIEW_EDIT,
  'Brooklyn.Projections.Forecast': VIEW_EDIT,
  'Brooklyn.Projections.Goal': VIEW_EDIT,
  'Brooklyn.Projections.Report': VIEW_ONLY,
  Projections: VIEW_EDIT,
  Users: FULL,
  Roles: FULL,
  Agency: FULL,
  Accounts: FULL,
  Sites: FULL,
  // Add and Delete are enforced by AddInvestor/DeleteInvestor and AddVendor/DeleteVendor, so the
  // checkboxes have to exist for the permission to be grantable and revocable. The client marked
  // them for removal, but a permission the API honours and the screen hides cannot be managed.
  Suppliers: FULL,
  Reps: FULL,
  Vendors: FULL,
  Resources: FULL,
  'Account Types': FULL,
  RetailProgram: FULL,
  'Brooklyn.RetailProgram': FULL,
  Badges: FULL,
  'Under Review': FULL,
  Terms: FULL,
  Maintenance: VIEW_EDIT,
  Announcements: FULL,
  General: VIEW_EDIT,
};

const PAYBEV_CAPS: Record<string, PermissionCaps> = {
  Summary: VIEW_ONLY,
  Activity: VIEW_EDIT,
  Investments: FULL,
  Expenses: FULL,
  'Account Statements': VIEW_ONLY,
  'Activity Export': VIEW_ONLY,
  'Outstanding Statement': VIEW_ONLY,
  'Internal Reports': VIEW_ONLY,
  Reports: VIEW_ONLY,
  Projections: VIEW_EDIT,
  Investors: FULL,
  Vendors: FULL,
  'Program Periods': FULL,
  Tags: FULL,
  Users: FULL,
  Notifications: VIEW_EDIT,
  Tutorials: VIEW_ONLY,
  RetailProgram: FULL,
};

const SUPPLIER_CAPS: Record<string, PermissionCaps> = {
  ...PAYBEV_CAPS,
};

const MODULE_ALIASES: Record<PermissionModuleType, Record<string, string>> = {
  Brooklyn: {
    'Brooklyn.Overview': 'Overview',
    'Brooklyn.Cash Flow': 'Cash Flow',
    'Brooklyn.Dashboard': 'Dashboard',
    'Brooklyn.Investments': 'Investments',
    'Brooklyn.Expenses': 'Expenses',
    'Brooklyn.Transfers': 'Transfers',
    'Brooklyn.Reports': 'Reports',
    'Brooklyn.Users': 'Users',
    'Brooklyn.Vendors': 'Vendors',
  },
  Paybev: {},
  Supplier: {},
};

const CAPS_BY_TYPE: Record<PermissionModuleType, Record<string, PermissionCaps>> = {
  Brooklyn: BROOKLYN_CAPS,
  Paybev: PAYBEV_CAPS,
  Supplier: SUPPLIER_CAPS,
};

function resolveModuleType(moduleType: string | null | undefined): PermissionModuleType {
  if (moduleType === 'Paybev' || moduleType === 'Supplier') {
    return moduleType;
  }
  return 'Brooklyn';
}

function canonicalModuleName(moduleType: PermissionModuleType, module: string): string {
  return MODULE_ALIASES[moduleType]?.[module] ?? module;
}

export function getModuleCapabilities(
  moduleType: string | null | undefined,
  module: string
): PermissionCaps {
  const resolvedType = resolveModuleType(moduleType);
  const canonical = canonicalModuleName(resolvedType, module);
  const caps = CAPS_BY_TYPE[resolvedType][canonical];
  if (caps) {
    return caps;
  }

  const warnKey = `${resolvedType}:${module}`;
  if (!warnedUnknown.has(warnKey)) {
    warnedUnknown.add(warnKey);
    console.warn(
      `[PermissionDetails] Unknown module "${module}" (${resolvedType}); showing View only.`
    );
  }
  return VIEW_ONLY;
}

export function derivePermissionUiFlags(caps: PermissionCaps): PermissionUiFlags {
  const visibleActions = [caps.view, caps.add, caps.edit, caps.delete].filter(Boolean).length;
  return {
    _uiShowAll: visibleActions > 1,
    _uiShowView: caps.view,
    _uiShowAdd: caps.add,
    _uiShowEdit: caps.edit,
    _uiShowDelete: caps.delete,
  };
}

export function isAllVisibleGranted(child: PermissionFlagSource, caps: PermissionCaps): boolean {
  if (caps.view && !child.canView) {
    return false;
  }
  if (caps.add && !child.canAdd) {
    return false;
  }
  if (caps.edit && !child.canEdit) {
    return false;
  }
  if (caps.delete && !child.canDelete) {
    return false;
  }
  return caps.view || caps.add || caps.edit || caps.delete;
}

export function syncHasAllFromCaps(
  child: PermissionFlagSource,
  moduleType: string | null | undefined,
  module: string
): void {
  const caps = getModuleCapabilities(moduleType, module);
  child.hasAll = isAllVisibleGranted(child, caps);
}

export interface PermissionFlagSource {
  canView?: boolean;
  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  hasAll?: boolean;
}

/** Apply All toggle only to checkboxes that are visible for this module. */
export function setAllVisiblePermissionFlags(
  child: PermissionFlagSource,
  moduleType: string | null | undefined,
  module: string,
  checked: boolean
): void {
  const caps = getModuleCapabilities(moduleType, module);
  if (caps.view) {
    child.canView = checked;
  }
  if (caps.add) {
    child.canAdd = checked;
  }
  if (caps.edit) {
    child.canEdit = checked;
  }
  if (caps.delete) {
    child.canDelete = checked;
  }
  if (!checked) {
    child.hasAll = false;
    return;
  }
  syncHasAllFromCaps(child, moduleType, module);
}

/** Cascade uncheck / grant dependencies for visible checkboxes only. */
export function applyVisiblePermissionToggle(
  child: PermissionFlagSource,
  moduleType: string | null | undefined,
  module: string,
  checkbox: 'view' | 'add' | 'edit' | 'delete',
  checked: boolean
): void {
  const caps = getModuleCapabilities(moduleType, module);

  if (!checked) {
    child.hasAll = false;
    if (checkbox === 'view') {
      if (caps.add) {
        child.canAdd = false;
      }
      if (caps.edit) {
        child.canEdit = false;
      }
      if (caps.delete) {
        child.canDelete = false;
      }
      return;
    }
    if (checkbox === 'edit' && caps.delete) {
      child.canDelete = false;
      return;
    }
    if (checkbox === 'add') {
      if (caps.edit) {
        child.canEdit = false;
      }
      if (caps.delete) {
        child.canDelete = false;
      }
    }
    return;
  }

  if (checkbox === 'delete' && caps.delete) {
    child.canDelete = true;
    if (caps.edit) {
      child.canEdit = true;
    }
    if (caps.add) {
      child.canAdd = true;
    }
    if (caps.view) {
      child.canView = true;
    }
  } else if (checkbox === 'edit' && caps.edit) {
    child.canEdit = true;
    if (caps.add) {
      child.canAdd = true;
    }
    if (caps.view) {
      child.canView = true;
    }
  } else if (checkbox === 'add' && caps.add) {
    child.canAdd = true;
    if (caps.view) {
      child.canView = true;
    }
  } else if (checkbox === 'view' && caps.view) {
    child.canView = true;
  }

  syncHasAllFromCaps(child, moduleType, module);
}

export function isInvestmentModule(module: string): boolean {
  return module === 'Investments';
}

export function isPaymentModule(module: string): boolean {
  return module === 'Expenses';
}

export function isInvestorModule(module: string): boolean {
  return module === 'Investors';
}

export function isVendorModule(module: string): boolean {
  return module === 'Vendors';
}
